import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import moment from 'moment';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Checkbox, FormHelperText } from '@mui/material';

import { ReactComponent as ClockRewindIcon } from '../../../../../assets/icons/v2/clock-rewind.svg';
import { ChatChannelListContext } from '../../../../../chat';
import { InfoTag } from '../../../../../components/Tags';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useLogger from '../../../../../hooks/useLogger';
import UserContext from '../../../../../context/UserContext';
import FirebaseContext from '../../../../../context/FirebaseContext';
import FeedContext from '../../../../context/FeedContext';
import ChatContext from '../../../../context/ChatContext';
import LayoutContext from '../../../../layout/context/LayoutContext';
import feedType from '../../../../context/FeedContext/config/feedTypes';
import useMealPlanNavigation from '../../../../hooks/useMealPlanNavigation';
import { actionBarType } from '../../../../context/FeedContext/config/actionBarTypes';
import { OutreachType, DISENGAGEMENT_BROADCAST_TAG } from '../../../../constants/disengagementFilters';
import useSilenceToggle from '../../../../hooks/useSilenceToggle';
import useToast from '../../../../hooks/useToast';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { FeedActionBarContent } from '../FeedActionBar/FeedActionBar';

import { CHAT_REPLY_TASK_TYPES } from '../../../../Model/CoachTask';
import { buildQuotedMessageFromActivity } from './utils/buildQuotedMessageFromActivity';

import {
  FeedActionContainer,
  FeedActionButton,
  StyledWorkoutIcon,
  StyledTrackIcon,
  StyledMealIcon,
  StyledVideoReviewIcon,
  StyledChatIcon,
  ActionButtonsContainer,
  StyledSmartChatIcon,
  StyledBroadcastIcon,
  SilenceCheckboxContainer,
} from './styles';
import texts from './texts.json';

const BirthdayAlert = 'BIRTHDAY';

const SilenceCheckbox = ({ isSilenced, onToggle, disabled }) => (
  <SilenceCheckboxContainer>
    <Checkbox
      checked={isSilenced}
      onChange={onToggle}
      disabled={disabled}
      size="small"
    />
    <FormHelperText>{texts.SILENCE_BROADCASTS}</FormHelperText>
  </SilenceCheckboxContainer>
);

SilenceCheckbox.propTypes = {
  isSilenced: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const ActivityAction = ({ activity, clientUserDoc, onActivityActionClick }) => {
  const {
    selectedFeedType,
    setSelectedActionBarType,
    setSelectedActivity,
    setIsSmartFeedbackSelected,
  } = useContext(FeedContext);
  const {
    onQuickChatUserSelected,
    setChatModalOpen,
    setIsBroadcastModalOpen,
    setBroadcastInitialFilters,
    setPendingOutreachType,
    setPendingReply,
  } = useContext(ChatContext);
  const { navigateToNewAssignmentPage, navigateToRefreshPage } = useMealPlanNavigation();
  const { isCoachAndCoachAssistant, isCoach, isCoachAssistant } = useSessionStore();
  const { userId: coachId } = useContext(UserContext);
  const { firebase } = useContext(FirebaseContext);
  const { setActiveUserId } = useContext(ChatChannelListContext);
  const { setIsChatPanelOpen, setRightPanelContent } = useContext(LayoutContext);
  const { logEvent } = useLogger();
  const { showToast } = useToast();
  const { isSilenced, handleSilenceToggle } = useSilenceToggle(clientUserDoc);
  const [isReassigning, setIsReassigning] = useState(false);
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);

  const onReassignClick = () => {
    if (!activity?.user || !activity?.trackAssignmentId) return;
    logEvent('coach_track_reassignment_tapped', {
      user_id: activity.user,
      coach_id: coachId,
      old_track_id: activity.trackAssignmentId,
    });
    setShowReassignConfirm(true);
  };

  const onConfirmReassign = async () => {
    setShowReassignConfirm(false);
    setIsReassigning(true);
    try {
      const response = await firebase.remote('workout-removeTrackAssignment', {
        trackAssignmentId: activity.trackAssignmentId,
        requestNewTrack: true,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || texts.reassignError);
      }
      const responseData = await response.json();
      if (responseData.noEligibleTrack) {
        showToast(texts.reassignNoEligibleTrack, { warning: true });
      } else {
        showToast(texts.reassignSuccess);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          trackAssignmentId: activity.trackAssignmentId,
          userId: activity.user,
        },
      });
      showToast(texts.reassignError, { error: true });
    } finally {
      setIsReassigning(false);
    }
  };

  const handleCheckInFeedbackClick = (isSmart = false) => {
    setIsSmartFeedbackSelected(false);
    setChatModalOpen(false);
    setIsChatPanelOpen(true);
    setRightPanelContent(<FeedActionBarContent />, { openPanel: false });
    setSelectedActionBarType(actionBarType.CHECK_IN_FEEDBACK);
    setSelectedActivity(activity);
    if (isSmart) {
      setIsSmartFeedbackSelected(true);
    }
  };

  const handleNeedNewMealPlanClick = () => {
    setChatModalOpen(false);
    setIsChatPanelOpen(false);
    setSelectedActivity(activity);
    // The notification is raised per product, so it preselects the plan it was about.
    const navigationOptions = { state: { isRoutedFromFeed: true }, productId: activity.productId };
    if (activity.isRefresh) {
      navigateToRefreshPage(activity.user, true, navigationOptions);
    } else {
      navigateToNewAssignmentPage(activity.user, true, navigationOptions);
    }
  };

  const ChatButton = () => (
    <FeedActionButton
      startIcon={<StyledChatIcon />}
      onClick={() => {
        setIsChatPanelOpen(true);
        onQuickChatUserSelected(activity.user);
        setActiveUserId(activity.user);
      }}
    >
      {texts.CHAT}
    </FeedActionButton>
  );

  switch (selectedFeedType) {
    case feedType.WORKOUT_FEEDBACK_REQUEST:
      if (!activity.workoutVideoAvailable && !activity.isExerciseVideoFeedback) {
        return null;
      }
      return (
        <FeedActionButton
          startIcon={<StyledVideoReviewIcon />}
          variant="outlined"
          color="success"
          onClick={() => {
            setIsChatPanelOpen(true);
            const {
              user,
              workoutAssignmentId,
              isExerciseVideoFeedback: isV2ExerciseVideoFeedback,
              workoutVideoAvailable: isV1WorkoutVideoAvailable,
            } = activity;

            // This is the app v1 feedback and we need wokoutAssignmentId and user
            if (isV1WorkoutVideoAvailable && (!user || !workoutAssignmentId)) {
              const message = `Feed type ${feedType.WORKOUT_COMPLETE} activity doesn't have`
                + ' "user" and/or "workoutAssignmentId" when workout video is available';

              Sentry.captureMessage(message, {
                extra: {
                  user: user || 'undefined',
                  workoutAssignmentId: workoutAssignmentId || 'undefined',
                },
              });
            }

            // If the activity is a exercise video feedback from App V2,
            // we need to set the action bar type to EXERCISE_VIDEO_FEEDBACK
            const currentActionBarType = isV2ExerciseVideoFeedback
              ? actionBarType.EXERCISE_VIDEO_FEEDBACK
              : actionBarType.MOBILE_VIEW;
            setChatModalOpen(false);
            setRightPanelContent(<FeedActionBarContent />, { openPanel: false });
            setSelectedActionBarType(currentActionBarType);
            setSelectedActivity(activity);
          }}
        >
          {texts.VIDEO_REVIEW}
        </FeedActionButton>
      );
    case feedType.SPECIAL:
      if (activity.alertType === BirthdayAlert) {
        return (
          <ChatButton />
        );
      }
      return null;
    case feedType.PAUSE_REQUEST:
      return <ChatButton />;
    case feedType.NEED_NEW_WORKOUTS:
      return (
        <FeedActionContainer>
          <ActionButtonsContainer>
            <FeedActionButton
              startIcon={<StyledWorkoutIcon />}
              variant="outlined"
              color="success"
              onClick={() => {
                setIsChatPanelOpen(false);
                onActivityActionClick({
                  feedType: feedType.NEED_NEW_WORKOUTS,
                  userId: activity.user,
                  productId: activity.productId,
                });
              }}
            >
              {texts.ASSIGN_WORKOUT}
            </FeedActionButton>
            {isCoachAssistant && (
              <FeedActionButton
                startIcon={<StyledTrackIcon />}
                variant="outlined"
                color="success"
                // Without the client's doc the modal can't ask which product this is for.
                disabled={!clientUserDoc}
                onClick={() => onActivityActionClick({
                  feedType: feedType.ASSIGN_TRACK,
                  userId: activity.user,
                  productId: activity.productId,
                  clientUserDoc,
                })}
              >
                {texts.ASSIGN_TRACK}
              </FeedActionButton>
            )}
          </ActionButtonsContainer>
          {!!activity?.trackAutomationTime && isCoachAssistant && (
            <InfoTag>
              <ClockRewindIcon />
              {format(texts.actionInfo.automationTime, {
                time: moment(activity?.trackAutomationTime?.toDate?.()).fromNow(),
              })}
            </InfoTag>
          )}
        </FeedActionContainer>
      );
    case feedType.CHECK_IN:
      return (
        <FeedActionContainer>
          <ActionButtonsContainer>
            <FeedActionButton
              startIcon={<StyledChatIcon />}
              variant="outlined"
              color="success"
              onClick={() => handleCheckInFeedbackClick()}
            >
              {texts.CHECK_IN_FEEDBACK}
            </FeedActionButton>
            {(!isCoach || isCoachAndCoachAssistant) && (
              <FeedActionButton
                startIcon={<StyledSmartChatIcon />}
                variant="outlined"
                color="success"
                onClick={() => handleCheckInFeedbackClick(true)}
              >
                {texts.GENERATE_FEEDBACK}
              </FeedActionButton>
            )}
          </ActionButtonsContainer>
          {isCoachAssistant && (
            <SilenceCheckbox
              isSilenced={isSilenced}
              onToggle={handleSilenceToggle}
              disabled={!clientUserDoc}
            />
          )}
        </FeedActionContainer>
      );
    case feedType.NEED_NEW_MEAL_PLAN:
      return (
        <FeedActionContainer>
          <FeedActionButton
            startIcon={<StyledMealIcon />}
            variant="outlined"
            color="success"
            onClick={() => handleNeedNewMealPlanClick()}
          >
            { activity.isRefresh ? texts.REFRESH_MEAL_PLAN : texts.ASSIGN_MEAL_PLAN}
          </FeedActionButton>
          {!!activity?.mealPlanAutomationTime && isCoachAssistant && (
            <InfoTag>
              <ClockRewindIcon />
              {format(texts.actionInfo.automationTime, {
                time: moment(activity?.mealPlanAutomationTime?.toDate?.()).fromNow(),
              })}
            </InfoTag>
          )}
        </FeedActionContainer>
      );
    case feedType.USER_PREFERENCE_CHANGE:
      return (
        <>
          <FeedActionContainer>
            <ActionButtonsContainer>
              {!activity?.isTrackRemoved && (
                <FeedActionButton
                  startIcon={<StyledTrackIcon />}
                  variant="outlined"
                  color="success"
                  disabled={isReassigning || !activity?.trackAssignmentId}
                  onClick={onReassignClick}
                >
                  {texts.REASSIGN_TRACK}
                </FeedActionButton>
              )}
              <FeedActionButton
                startIcon={<StyledTrackIcon />}
                variant="outlined"
                color="success"
                // Without the client's doc the modal can't ask which product this is for.
                disabled={!clientUserDoc}
                onClick={() => onActivityActionClick({
                  feedType: feedType.ASSIGN_TRACK,
                  userId: activity.user,
                  productId: activity.productId,
                  clientUserDoc,
                })}
              >
                {texts.ASSIGN_TRACK}
              </FeedActionButton>
            </ActionButtonsContainer>
          </FeedActionContainer>
          <ConfirmDialog
            isOpen={showReassignConfirm}
            onConfirm={onConfirmReassign}
            onCancel={() => setShowReassignConfirm(false)}
            dialogTexts={{
              title: texts.reassignConfirm.title,
              content: texts.reassignConfirm.content,
              confirm: texts.reassignConfirm.confirm,
              cancel: texts.reassignConfirm.cancel,
            }}
          />
          <LoadingOverlay isLoading={isReassigning} loadingText={texts.reassignLoading} />
        </>
      );
    case feedType.DISENGAGED_CLIENT: {
      return (
        <FeedActionContainer>
          <ActionButtonsContainer>
            <FeedActionButton
              startIcon={<StyledChatIcon />}
              variant="outlined"
              color="success"
              onClick={() => {
                setIsChatPanelOpen(true);
                onQuickChatUserSelected(activity.user);
                setActiveUserId(activity.user);
                setPendingOutreachType(OutreachType.MANUAL_TARGETED);
                logEvent('disengaged_client_message_sent', {
                  ac_id: coachId,
                  client_id: activity.user,
                  disengagement_criteria: activity.disengagementCriteria,
                  outreach_type: OutreachType.MANUAL_TARGETED,
                });
              }}
            >
              {texts.SEND_MESSAGE}
            </FeedActionButton>
            <FeedActionButton
              startIcon={<StyledBroadcastIcon />}
              variant="outlined"
              color="success"
              onClick={() => {
                setBroadcastInitialFilters([DISENGAGEMENT_BROADCAST_TAG]);
                setIsChatPanelOpen(false);
                setIsBroadcastModalOpen(true);
              }}
            >
              {texts.BROADCAST}
            </FeedActionButton>
          </ActionButtonsContainer>
          {isCoachAssistant && (
            <SilenceCheckbox
              isSilenced={isSilenced}
              onToggle={handleSilenceToggle}
              disabled={!clientUserDoc}
            />
          )}
        </FeedActionContainer>
      );
    }
    case feedType.COACH_TASK:
      if (!CHAT_REPLY_TASK_TYPES.includes(activity.taskType)) {
        return null;
      }
      return (
        <FeedActionButton
          startIcon={<StyledChatIcon />}
          variant="outlined"
          color="success"
          onClick={() => {
            const quoted = buildQuotedMessageFromActivity(activity);
            if (quoted) setPendingReply({ userId: activity.user, quotedMessage: quoted });
            setIsChatPanelOpen(true);
            onQuickChatUserSelected(activity.user);
            setActiveUserId(activity.user);
          }}
        >
          {texts.OPEN_CHAT_AND_REPLY}
        </FeedActionButton>
      );
    default:
      return null;
  }
};

ActivityAction.propTypes = {
  activity: PropTypes.object,
  clientUserDoc: PropTypes.object,
  onActivityActionClick: PropTypes.func.isRequired,
};

ActivityAction.defaultProps = {
  activity: {},
  clientUserDoc: null,
};

export default compose(
  observer,
)(ActivityAction);
